/**
 * Formatting utilities for the Simple Writing Replay UI.
 *
 * These formatters provide teacher-friendly display of durations and timestamps
 * in the replay interface.
 */

/**
 * Duration thresholds in milliseconds.
 */
const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;

/**
 * Format a duration in milliseconds to a human-readable string.
 *
 * Follows these thresholds:
 * - < 1 second: "Instant"
 * - 1-59 seconds: "X sec"
 * - 1-59 minutes: "X min"
 * - >= 60 minutes: "Xh Xm"
 *
 * @param ms - Duration in milliseconds
 * @returns Human-readable duration string
 *
 * @example
 * formatDuration(500)     // "Instant"
 * formatDuration(4000)    // "4 sec"
 * formatDuration(180000)  // "3 min"
 * formatDuration(3660000) // "1h 1m"
 */
export function formatDuration(ms: number): string {
  // Handle edge cases
  if (!Number.isFinite(ms) || ms < SECOND_MS) {
    return "Instant";
  }

  const totalSeconds = Math.floor(ms / SECOND_MS);
  const totalMinutes = Math.floor(ms / MINUTE_MS);
  const totalHours = Math.floor(ms / HOUR_MS);

  // Hours range (>= 60 minutes)
  if (totalMinutes >= 60) {
    const remainingMinutes = totalMinutes % 60;
    return `${totalHours}h ${remainingMinutes}m`;
  }

  // Minutes range (1-59 minutes)
  if (totalMinutes >= 1) {
    return `${totalMinutes} min`;
  }

  // Seconds range (1-59 seconds)
  return `${totalSeconds} sec`;
}

/**
 * Format an ISO timestamp to a teacher-friendly relative/friendly string.
 *
 * Display rules based on age:
 * - Same day: "2:34 PM" (time only)
 * - Yesterday: "Yesterday 2:34 PM"
 * - This week (2-6 days ago): "Monday 2:34 PM" (weekday + time)
 * - Older (same year): "Dec 15, 2:34 PM" (month day + time)
 * - Different year: "Dec 15, 2024, 2:34 PM" (month day year + time)
 *
 * @param isoString - ISO 8601 timestamp string
 * @returns Human-readable timestamp string
 *
 * @example
 * // Same day
 * formatTimestamp("2025-12-20T14:34:00") // "2:34 PM"
 *
 * // Yesterday
 * formatTimestamp("2025-12-19T14:34:00") // "Yesterday 2:34 PM"
 *
 * // This week
 * formatTimestamp("2025-12-18T14:34:00") // "Thursday 2:34 PM"
 *
 * // Older
 * formatTimestamp("2025-12-10T14:34:00") // "Dec 10, 2:34 PM"
 */
export function formatTimestamp(isoString: string): string {
  const date = new Date(isoString);

  // Handle invalid dates
  if (isNaN(date.getTime())) {
    return "Invalid date";
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Calculate difference in days
  const diffTime = today.getTime() - dateDay.getTime();
  const diffDays = Math.floor(diffTime / (24 * 60 * 60 * 1000));

  // Format time portion consistently
  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  // Same day (today)
  if (diffDays === 0) {
    return timeStr;
  }

  // Yesterday
  if (diffDays === 1) {
    return `Yesterday ${timeStr}`;
  }

  // This week (2-6 days ago)
  if (diffDays >= 2 && diffDays <= 6) {
    const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
    return `${weekday} ${timeStr}`;
  }

  // Different year - include year
  if (date.getFullYear() !== now.getFullYear()) {
    const dateStr = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${dateStr}, ${timeStr}`;
  }

  // Older than a week (same year) - show month and day
  const dateStr = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  return `${dateStr}, ${timeStr}`;
}
